import { useState, useCallback, useEffect, useRef } from 'react';

import { FiGlobe } from 'react-icons/fi';

import { StreamingData } from '@common/types/api';

import * as S from './styles';

type LanguageSelectorProps = {
  streamings: StreamingData[];
  currentIndex: number;
  onSelect: (index: number) => void;
  onOpenChange?: (open: boolean) => void;
};

const LanguageSelector = ({
  streamings,
  currentIndex,
  onSelect,
  onOpenChange,
}: LanguageSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLang = streamings[currentIndex]?.streaming_lang ?? '';

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      onOpenChange?.(next);
      return next;
    });
  }, [onOpenChange]);

  const handleSelect = useCallback(
    (index: number) => () => {
      onSelect(index);
      setIsOpen(false);
      onOpenChange?.(false);
    },
    [onSelect, onOpenChange],
  );

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        onOpenChange?.(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onOpenChange]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setIsOpen(false);
        onOpenChange?.(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onOpenChange]);

  return (
    <S.Container ref={containerRef}>
      <S.ToggleButton type="button" onClick={toggle}>
        <FiGlobe />
        <span>{currentLang}</span>
      </S.ToggleButton>

      {isOpen && (
        <S.Dropdown>
          {streamings.map((streaming, index) => (
            <S.Option
              key={streaming.streaming_lang}
              $active={currentIndex === index}
              type="button"
              onClick={handleSelect(index)}
            >
              {streaming.streaming_lang}
            </S.Option>
          ))}
        </S.Dropdown>
      )}
    </S.Container>
  );
};

export default LanguageSelector;
